/* eslint-disable no-lonely-if */
import { CopilotActions } from 'actions/actionTypes';
import { COPILOT_HOME_PRESENTATION_ACTION } from 'actions/copilotHomePresentation';
import {
  buildOmnisearchCacheKey,
  buildOmnisearchLoadMoreKey
} from 'actions/copilotOmnisearch';

import {
  mergeCopilotChatDetailMessages,
  normalizeCopilotContextType
} from 'helpers/copilot';

import { MESSAGE_STATUS_OPTIONS } from 'constants/copilot';

const initialChatHistoryPagination = {
  next: null,
  count: 0,
  pageSize: 30,
  initialLoaded: false,
  fetchingMore: false,
  error: null
};

const hasSelectedContext = value => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return Object.values(value).some(entry => {
    if (Array.isArray(entry)) {
      return entry.length > 0;
    }
    if (entry && typeof entry === 'object') {
      return Object.keys(entry).length > 0;
    }
    return Boolean(entry);
  });
};

const deriveCopilotLoadingFromActiveRuns = runs =>
  Object.values(runs || {}).some(
    run => run?.status === 'pending' || run?.status === 'running'
  );

const reconcileCancelledRunMessages = (
  existingMessages,
  authoritativeMessages,
  { runUuid, assistantMessageUuid, optimisticUserMessageClientRequestId }
) => {
  const reconciledMessages = (existingMessages || []).filter(
    message =>
      message.copilotRunUuid !== runUuid &&
      (!assistantMessageUuid || message.uuid !== assistantMessageUuid) &&
      (!optimisticUserMessageClientRequestId ||
        message.clientRequestId !== optimisticUserMessageClientRequestId)
  );

  (authoritativeMessages || []).forEach(message => {
    const messageIndex = message?.uuid
      ? reconciledMessages.findIndex(
          existingMessage => existingMessage.uuid === message.uuid
        )
      : -1;

    if (messageIndex === -1) {
      reconciledMessages.push(message);
      return;
    }

    reconciledMessages[messageIndex] = message;
  });

  return reconciledMessages;
};

const initialCopilotState = {
  sessions: {
    data: {}
  },
  /** True after a successful `GET /api/v1/copilot/chats/` (unified list). Cleared on `LOGOUT`. */
  unifiedCopilotChatListLoaded: false,
  chatHistoryPagination: initialChatHistoryPagination,
  /**
   * Per chat: `true` after messages were hydrated (detail GET, merge, or WebSocket), so re-selecting
   * the thread does not call `GET /chats/:uuid` again. Keys removed on `DELETE_COPILOT_CHAT_SUCCESS` / `LOGOUT`.
   */
  unifiedCopilotChatDetailsLoaded: {},
  /**
   * Chat uuids whose cached detail is context-stale (context was added via a send, which is not
   * written back to the cached session). `fetchCopilotChatDetail` revalidates these on next select
   * instead of no-op'ing; cleared on `FETCH_COPILOT_CHAT_DETAIL_SUCCESS`.
   */
  copilotChatDetailContextStaleUuids: {},
  fetching: false,
  /** `GET /chats/:uuid` in flight (messages hydrate separately from the list). */
  fetchingCopilotChatDetailUuid: null,
  loading: false,
  error: null,
  windowMode: 'embedded',
  homePresentation: 'compact',
  /**
   * When true, the next `updateContextFromUrl` tick must not call `emptyCopilotChatContext` for a
   * spurious `hasPathOrMaterialSearchChanged` (e.g. Copilot remount after float → full on meeting).
   * Set by {@link SET_COPILOT_WINDOW_MODE_FROM_SHELL}; cleared after consumption or on empty context.
   */
  urlShellSyncPreserveChat: false,
  chatContext: null,
  /**
   * @-mention omnisearch over WebSocket (`message_type: omnisearch`). `activeRequestId`
   * is the client request id; responses with a different id are dropped as stale.
   * `requestSearchType` mirrors `data.search_type` (Add context tab, or `my_context` for composer
   * @ empty query); `null` for global @-mention omnisearch (non-empty query).
   */
  contextOmnisearch: {
    loading: false,
    activeRequestId: null,
    requestQuery: null,
    requestSearchType: null,
    requestFilterSignature: null,
    results: null,
    resultMetadata: {}
  },
  /**
   * Last successful omnisearch `results` by `search_type::query::filterSignature` (see
   * `buildOmnisearchCacheKey` in `actions/copilotOmnisearch.js`). Used to rehydrate without a second
   * WebSocket round-trip when switching Add context tabs or revisiting the same query/filter state.
   */
  contextOmnisearchCache: {},
  /**
   * Per-category "Load more" pagination for @-mention omnisearch (backend requires `search_type`
   * whenever `page` is sent, so this is always scoped to exactly one category — never the combined
   * grouped search). Keyed by `buildOmnisearchLoadMoreKey(searchType, query, filterSignature)`, so a
   * new keystroke or filter change naturally starts a fresh bucket. Kept separate from
   * `contextOmnisearch`'s single active-request slot so a load-more request can never be dropped by /
   * corrupt the primary grouped response (see SEND/RECEIVE_COPILOT_OMNISEARCH_PAGE below).
   * Bucket shape: `{ loading, page, hasNext, rows }`.
   */
  contextOmnisearchLoadMore: {},
  /**
   * FIFO queue of `contextOmnisearchLoadMore` keys awaiting a response, in send order. The backend's
   * paginated response (`{count, results}`) echoes neither a `request_id` nor `search_type` — there is
   * nothing in the payload to correlate a response back to the request that produced it. Since this is
   * one ordered WebSocket connection, responses are assumed to arrive in the same order their requests
   * were sent; `RECEIVE_COPILOT_OMNISEARCH_PAGE` shifts the oldest entry off this queue and applies the
   * response to that bucket. Best-effort: if the backend ever processes/replies out of order this can
   * misattribute a page to the wrong category, but there is no other signal available.
   */
  contextOmnisearchLoadMorePendingKeys: [],
  activeCopilotRunsByChatUuid: {},
  floatingWindow: {
    position: null,
    size: null
  },
  /**
   * Persisted Add-context / switch-rail mention state for the active chat session (e.g. survives
   * CopilotTextArea remount when toggling embedded ↔ floating). Cleared with chat context / session change.
   */
  composerContextMentionUi: null
};

const copilot = (state = initialCopilotState, action) => {
  switch (action.type) {
    case CopilotActions.SEND_COPILOT_OMNISEARCH: {
      const nextQ = String(action.query ?? '').trim();
      const nextSt = action.searchType ?? null;
      const nextFs = action.filterSignature ?? '';
      const prevQ = String(state.contextOmnisearch?.requestQuery ?? '').trim();
      const prevSt = state.contextOmnisearch?.requestSearchType ?? null;
      const prevFs = state.contextOmnisearch?.requestFilterSignature ?? '';
      const sameRequestKey =
        nextQ === prevQ && nextSt === prevSt && nextFs === prevFs;
      return {
        ...state,
        contextOmnisearch: {
          ...state.contextOmnisearch,
          loading: true,
          activeRequestId: action.requestId,
          requestQuery: action.query,
          requestSearchType: nextSt,
          requestFilterSignature: nextFs,
          // Keep previous hits visible while a refetch of the *same* query+type+filter is in flight (avoids empty flash).
          results: sameRequestKey ? state.contextOmnisearch.results : null,
          resultMetadata: sameRequestKey
            ? state.contextOmnisearch.resultMetadata
            : {}
        },
        // A same-key refetch keeps its accumulated rows (so the list does not shrink under the
        // user), but any in-flight page is abandoned: the pending queue is cleared unconditionally
        // below, so a page response arriving after this point is dropped. Clear each bucket's
        // `loading` too, or that dropped response leaves the "Load more" row spinning forever.
        contextOmnisearchLoadMore: sameRequestKey
          ? Object.fromEntries(
              Object.entries(state.contextOmnisearchLoadMore ?? {}).map(
                ([bucketKey, bucket]) => [
                  bucketKey,
                  bucket?.loading ? { ...bucket, loading: false } : bucket
                ]
              )
            )
          : {},
        // Always emptied, even for a same-key refetch: a paginated response is indistinguishable
        // in shape from a single-`search_type` primary one (see `isPaginatedOmnisearchResponse` in
        // `helpers/websockets.js`), so keeping this empty while a primary is in flight is what
        // stops a primary response being appended to a load-more bucket.
        contextOmnisearchLoadMorePendingKeys: []
      };
    }
    case CopilotActions.HYDRATE_COPILOT_OMNISEARCH_FROM_CACHE: {
      const nextQ = String(action.requestQuery ?? '').trim();
      const nextSt = action.requestSearchType ?? null;
      const nextFs = action.requestFilterSignature ?? '';
      return {
        ...state,
        contextOmnisearch: {
          ...state.contextOmnisearch,
          loading: false,
          activeRequestId: null,
          requestQuery: nextQ,
          requestSearchType: nextSt,
          requestFilterSignature: nextFs,
          results: action.results,
          resultMetadata: action.results?.resultMetadata ?? {}
        },
        contextOmnisearchLoadMore: {},
        contextOmnisearchLoadMorePendingKeys: []
      };
    }
    case CopilotActions.RECEIVE_COPILOT_OMNISEARCH: {
      const responseRequestIdRaw =
        action.data?.requestId ?? action.data?.request_id;
      const responseRequestId =
        responseRequestIdRaw != null ? String(responseRequestIdRaw) : null;
      const primaryId = state.contextOmnisearch?.activeRequestId ?? null;
      if (
        responseRequestId != null &&
        primaryId != null &&
        responseRequestId !== String(primaryId)
      ) {
        return state;
      }
      const {
        requestId: _rid,
        meeting,
        deal,
        user,
        team,
        playlist,
        company,
        resultMetadata,
        contextTypeOrder
      } = action.data || {};
      const resultsPayload = {
        meeting,
        deal,
        user,
        team,
        playlist,
        company,
        resultMetadata,
        contextTypeOrder
      };
      const cacheQ = String(state.contextOmnisearch?.requestQuery ?? '').trim();
      const cacheSt = state.contextOmnisearch?.requestSearchType ?? null;
      const cacheFs = state.contextOmnisearch?.requestFilterSignature ?? '';
      const cacheKey = buildOmnisearchCacheKey(cacheQ, cacheSt, cacheFs);
      return {
        ...state,
        contextOmnisearch: {
          ...state.contextOmnisearch,
          loading: false,
          results: resultsPayload,
          resultMetadata: resultMetadata ?? {}
        },
        contextOmnisearchCache: {
          ...state.contextOmnisearchCache,
          [cacheKey]: { results: resultsPayload }
        }
      };
    }
    case CopilotActions.CLEAR_COPILOT_OMNISEARCH: {
      if (action.preserveDefaultOmnisearch) {
        return {
          ...state,
          contextOmnisearch: {
            ...state.contextOmnisearch,
            loading: false,
            activeRequestId: null
            // requestQuery + results: keep (cache default empty-query response for the next open)
          },
          contextOmnisearchLoadMore: {},
          contextOmnisearchLoadMorePendingKeys: []
        };
      }
      return {
        ...state,
        contextOmnisearch: {
          loading: false,
          activeRequestId: null,
          requestQuery: null,
          requestSearchType: null,
          results: null,
          resultMetadata: {}
        },
        contextOmnisearchCache: {},
        contextOmnisearchLoadMore: {},
        contextOmnisearchLoadMorePendingKeys: []
      };
    }
    case CopilotActions.SEND_COPILOT_OMNISEARCH_PAGE: {
      const key = buildOmnisearchLoadMoreKey(
        action.searchType,
        action.query,
        action.filterSignature
      );
      const existingBucket = state.contextOmnisearchLoadMore?.[key];
      return {
        ...state,
        contextOmnisearchLoadMore: {
          ...state.contextOmnisearchLoadMore,
          [key]: {
            loading: true,
            page: action.page,
            pageSize: action.pageSize,
            hasNext: existingBucket?.hasNext ?? true,
            rows: existingBucket?.rows ?? []
          }
        },
        // FIFO: the backend's paginated response has nothing to correlate it back to this request
        // (no request_id, no search_type echo) — see the field's doc comment above.
        contextOmnisearchLoadMorePendingKeys: [
          ...(state.contextOmnisearchLoadMorePendingKeys ?? []),
          key
        ]
      };
    }
    case CopilotActions.RECEIVE_COPILOT_OMNISEARCH_PAGE: {
      const pendingKeys = state.contextOmnisearchLoadMorePendingKeys ?? [];
      const [matchingKey, ...restPendingKeys] = pendingKeys;
      const buckets = state.contextOmnisearchLoadMore ?? {};
      if (matchingKey == null || buckets[matchingKey] == null) {
        // Nothing was pending (stale/superseded response, e.g. query changed and buckets were
        // already cleared) — drop silently.
        return state;
      }
      const bucket = buckets[matchingKey];
      const results = Array.isArray(action.data?.results)
        ? action.data.results
        : [];
      // De-dupe by uuid against already-loaded rows (and within this page),
      // mirroring `filterMeetings.merge` — an overlapping or re-delivered page
      // must not append duplicate rows. Rows without a uuid are kept as-is.
      const existingRows = bucket.rows ?? [];
      const seenUuids = new Set(
        existingRows.map(row => row?.uuid).filter(Boolean)
      );
      const dedupedNewRows = results.filter(row => {
        const uuid = row?.uuid;
        if (!uuid) {
          return true;
        }
        if (seenUuids.has(uuid)) {
          return false;
        }
        seenUuids.add(uuid);
        return true;
      });
      // Backend does not send `has_next` (only `count` + `results`) — derive it: no results at all
      // means no more pages regardless of count; otherwise compare how much the server has served
      // in total against `count`, defaulting to "assume more" when neither signal is available.
      const hasNext = (() => {
        if (typeof action.data?.hasNext === 'boolean') {
          return action.data.hasNext;
        }
        if (results.length === 0) {
          return false;
        }
        if (typeof action.data?.count === 'number') {
          const { pageSize } = bucket;
          if (typeof pageSize === 'number' && pageSize > 0) {
            return bucket.page * pageSize < action.data.count;
          }
          return true;
        }
        return true;
      })();
      return {
        ...state,
        contextOmnisearchLoadMorePendingKeys: restPendingKeys,
        contextOmnisearchLoadMore: {
          ...buckets,
          [matchingKey]: {
            ...bucket,
            loading: false,
            hasNext,
            rows: [...existingRows, ...dedupedNewRows]
          }
        }
      };
    }
    case CopilotActions.CLEAR_COPILOT_OMNISEARCH_PAGE: {
      return {
        ...state,
        contextOmnisearchLoadMore: {},
        contextOmnisearchLoadMorePendingKeys: []
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_REQUEST: {
      const isFetchingMore = Boolean(action.next);
      const resetPagination = action.reset
        ? initialChatHistoryPagination
        : state.chatHistoryPagination;

      return {
        ...state,
        fetching: true,
        loading: isFetchingMore ? state.loading : true,
        error: null,
        chatHistoryPagination: {
          ...resetPagination,
          pageSize: action.pageSize ?? resetPagination.pageSize,
          fetchingMore: isFetchingMore,
          error: null
        }
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_SUCCESS: {
      const sessionsFromResponse = {};
      Object.entries(action.response || {}).forEach(([uuid, chat]) => {
        const prev = state.sessions.data[uuid] || {};
        sessionsFromResponse[uuid] = {
          ...prev,
          ...chat,
          contextType: normalizeCopilotContextType(chat.contextType),
          ...(action.preserveExistingSessions &&
          hasSelectedContext(prev.selectedContext) &&
          !hasSelectedContext(chat.selectedContext)
            ? { selectedContext: prev.selectedContext }
            : {}),
          ...(action.preserveExistingSessions &&
          hasSelectedContext(prev.selectedMentions) &&
          !hasSelectedContext(chat.selectedMentions)
            ? { selectedMentions: prev.selectedMentions }
            : {}),
          // Unified list often omits messages or returns `[]` per row. Never clobber a hydrated
          // in-memory thread (optimistic user + WebSocket) with an empty list payload.
          messages: (() => {
            if (Array.isArray(chat.messages) && chat.messages.length > 0) {
              return chat.messages;
            }
            if (prev.messages && prev.messages.length > 0) {
              return prev.messages;
            }
            return Array.isArray(chat.messages)
              ? chat.messages
              : chat.messages ?? prev.messages;
          })()
        };
      });
      const markDetailUuids = action.markDetailLoadedForUuids;
      const nextDetailsLoaded = { ...state.unifiedCopilotChatDetailsLoaded };
      if (Array.isArray(markDetailUuids)) {
        markDetailUuids.forEach(id => {
          if (id) {
            nextDetailsLoaded[id] = true;
          }
        });
      }
      const isUnifiedListFetch = action.unifiedListFetch === true;
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...(action.reset ? {} : state.sessions.data),
            ...sessionsFromResponse
          }
        },
        unifiedCopilotChatListLoaded:
          action.unifiedListFetch === true
            ? true
            : state.unifiedCopilotChatListLoaded,
        unifiedCopilotChatDetailsLoaded: nextDetailsLoaded,
        fetching: isUnifiedListFetch ? false : state.fetching,
        loading: isUnifiedListFetch ? false : state.loading,
        error: isUnifiedListFetch ? null : state.error,
        chatHistoryPagination: isUnifiedListFetch
          ? {
              ...state.chatHistoryPagination,
              next: action.pagination?.next ?? null,
              count:
                action.pagination?.count ?? state.chatHistoryPagination.count,
              pageSize:
                action.pagination?.pageSize ??
                state.chatHistoryPagination.pageSize,
              initialLoaded: true,
              fetchingMore: false,
              error: null
            }
          : state.chatHistoryPagination
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_DETAIL_REQUEST: {
      return {
        ...state,
        fetchingCopilotChatDetailUuid: action.uuid,
        error: null
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_DETAIL_SUCCESS: {
      const { uuid, chat } = action;
      const prev = state.sessions.data[uuid] || {};
      const mergedMessages = mergeCopilotChatDetailMessages(
        prev.messages,
        chat?.messages
      );
      const normalized = {
        ...prev,
        ...chat,
        contextType: normalizeCopilotContextType(chat.contextType),
        messages: mergedMessages
      };
      const clearDetailFetching = state.fetchingCopilotChatDetailUuid === uuid;
      const nextContextStale = { ...state.copilotChatDetailContextStaleUuids };
      delete nextContextStale[uuid];
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [uuid]: normalized
          }
        },
        unifiedCopilotChatDetailsLoaded: {
          ...state.unifiedCopilotChatDetailsLoaded,
          [uuid]: true
        },
        copilotChatDetailContextStaleUuids: nextContextStale,
        fetchingCopilotChatDetailUuid: clearDetailFetching
          ? null
          : state.fetchingCopilotChatDetailUuid,
        error: null
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_DETAIL_FAILURE: {
      const clearDetailFetching =
        state.fetchingCopilotChatDetailUuid === action.uuid;
      // Mark detail "settled" so the UI does not spin forever; user can still see empty/error state.
      const failedUuid = action.uuid;
      const nextDetailsLoaded =
        failedUuid != null
          ? {
              ...state.unifiedCopilotChatDetailsLoaded,
              [failedUuid]: true
            }
          : state.unifiedCopilotChatDetailsLoaded;
      return {
        ...state,
        fetchingCopilotChatDetailUuid: clearDetailFetching
          ? null
          : state.fetchingCopilotChatDetailUuid,
        unifiedCopilotChatDetailsLoaded: nextDetailsLoaded,
        error: action.error ?? state.error
      };
    }
    case CopilotActions.CREATE_COPILOT_CHAT_REQUEST:
    case CopilotActions.UPDATE_COPILOT_CHAT_NAME_REQUEST:
    case CopilotActions.DELETE_COPILOT_CHAT_REQUEST: {
      return {
        ...state,
        fetching: false,
        loading: true,
        error: null
      };
    }
    case CopilotActions.CREATE_COPILOT_CHAT_SUCCESS: {
      const realUuid = action.response.uuid;
      const prev = state.sessions.data[realUuid] || {};
      const mergedMessages = mergeCopilotChatDetailMessages(
        prev.messages,
        action.response.messages
      );
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [realUuid]: {
              ...action.response,
              messages: mergedMessages,
              contextType: normalizeCopilotContextType(
                action.contextType ?? action.response.contextType
              ),
              contextUuid: action.contextUuid ?? action.response.contextUuid
            }
          }
        },
        fetching: false,
        loading: false,
        error: null
      };
    }
    case CopilotActions.UPDATE_COPILOT_CHAT_NAME_SUCCESS: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [action.response.uuid]: {
              ...state.sessions.data[action.response.uuid],
              name: action.response.name
            }
          }
        },
        fetching: false,
        loading: false,
        error: null
      };
    }
    case CopilotActions.DELETE_COPILOT_CHAT_SUCCESS: {
      const { [action.uuid]: removedSession, ...remainingSessions } =
        state.sessions.data;
      const { [action.uuid]: _rm, ...remainingDetailsLoaded } =
        state.unifiedCopilotChatDetailsLoaded;

      const shouldClearChatContext =
        state.chatContext != null &&
        state.chatContext.uuid != null &&
        state.chatContext.uuid === action.uuid;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: remainingSessions
        },
        unifiedCopilotChatDetailsLoaded: remainingDetailsLoaded,
        fetching: false,
        loading: false,
        error: null,
        ...(shouldClearChatContext
          ? { chatContext: null, composerContextMentionUi: null }
          : {})
      };
    }
    case CopilotActions.CREATE_COPILOT_CHAT_FAILURE: {
      return {
        ...state,
        fetching: false,
        loading: false,
        error: action.error
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_FAILURE: {
      const isFetchingMore = Boolean(action.next);
      return {
        ...state,
        fetching: false,
        loading: false,
        error: isFetchingMore ? state.error : action.error,
        chatHistoryPagination: {
          ...state.chatHistoryPagination,
          fetchingMore: false,
          error: isFetchingMore ? action.error : null
        }
      };
    }
    case CopilotActions.UPDATE_COPILOT_CHAT_NAME_FAILURE:
    case CopilotActions.DELETE_COPILOT_CHAT_FAILURE: {
      return {
        ...state,
        fetching: false,
        loading: false,
        error: action.error
      };
    }
    case CopilotActions.RECEIVE_COPILOT_MESSAGE: {
      const { chatUuid, uuid } = action.data || {};

      const existingSession = state.sessions.data[chatUuid] || {};
      const messages = existingSession.messages
        ? [...existingSession.messages]
        : [];
      const messageIndex = uuid
        ? messages.findIndex(msg => msg.uuid === uuid)
        : -1;

      const isStreamingMessage =
        action.data.status === MESSAGE_STATUS_OPTIONS.IN_PROGRESS;
      const isThinkingMessage =
        action.data.status === MESSAGE_STATUS_OPTIONS.THINKING;

      // Helper to create a new thinking message
      const createThinkingMessage = msgData => ({
        ...msgData,
        thinkingMessages: [
          {
            message: msgData.message || '',
            timestamp: msgData.created || new Date().toISOString()
          }
        ],
        thinkingStartTime: msgData.created,
        message: ''
      });

      if (uuid) {
        if (messageIndex === -1) {
          // New message — append
          messages.push(
            isThinkingMessage ? createThinkingMessage(action.data) : action.data
          );
        } else {
          // Update existing message
          const existingMessage = messages[messageIndex];

          if (isThinkingMessage) {
            // Append to thinkingMessages array
            const updatedThinkingMessages = existingMessage.thinkingMessages
              ? [...existingMessage.thinkingMessages]
              : [];

            updatedThinkingMessages.push({
              message: action.data.message || '',
              timestamp: action.data.created || new Date().toISOString()
            });

            messages[messageIndex] = {
              ...existingMessage,
              ...action.data,
              thinkingMessages: updatedThinkingMessages,
              thinkingStartTime:
                existingMessage.thinkingStartTime || action.data.created,
              message: existingMessage.message || ''
            };
          } else {
            // Completed message
            const wasThinking =
              existingMessage.status === MESSAGE_STATUS_OPTIONS.THINKING;
            const isNowNotThinking =
              wasThinking &&
              action.data.status !== MESSAGE_STATUS_OPTIONS.THINKING;

            let { thinkingEndTime } = existingMessage;

            if (isNowNotThinking && existingMessage.thinkingMessages?.length) {
              const lastStep =
                existingMessage.thinkingMessages[
                  existingMessage.thinkingMessages.length - 1
                ];
              const completionTime = action.data.created;

              thinkingEndTime =
                new Date(completionTime).getTime() >
                new Date(lastStep.timestamp).getTime()
                  ? completionTime
                  : new Date().toISOString();
            }

            messages[messageIndex] = {
              ...existingMessage,
              ...action.data,
              thinkingMessages: existingMessage.thinkingMessages || [],
              thinkingStartTime: existingMessage.thinkingStartTime,
              thinkingEndTime,
              message: wasThinking
                ? action.data.message || ''
                : (existingMessage.message || '') + (action.data.message || '')
            };
          }
        }
      } else {
        // Optimistic user message or message without UUID
        messages.push(action.data);
      }

      // Optimistically advance `lastMessageTime` so the history sidebar re-sorts before the next
      // `GET /chats/` refetch. Monotonic so streaming partials never drag it backward; a later
      // refetch reconciles to the authoritative server value.
      const candidateMessageTime =
        typeof action.data?.created === 'string' &&
        Number.isFinite(new Date(action.data.created).getTime())
          ? action.data.created
          : new Date().toISOString();
      const existingMessageTime = existingSession.lastMessageTime;
      const lastMessageTime =
        existingMessageTime &&
        new Date(existingMessageTime).getTime() >=
          new Date(candidateMessageTime).getTime()
          ? existingMessageTime
          : candidateMessageTime;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [chatUuid]: {
              ...existingSession,
              messages,
              lastMessageTime
            }
          }
        },
        unifiedCopilotChatDetailsLoaded: chatUuid
          ? {
              ...state.unifiedCopilotChatDetailsLoaded,
              [chatUuid]: true
            }
          : state.unifiedCopilotChatDetailsLoaded,
        loading: isThinkingMessage || isStreamingMessage
      };
    }
    case CopilotActions.RECEIVE_COPILOT_NAME_UPDATE: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [action.data.chatUuid]: {
              ...state.sessions.data[action.data.chatUuid],
              name: action.data.name
            }
          }
        }
      };
    }
    case CopilotActions.REHYDRATE_COPILOT_RUNS: {
      const mergedActiveRuns = {
        ...state.activeCopilotRunsByChatUuid,
        ...(action.runs || {})
      };
      return {
        ...state,
        activeCopilotRunsByChatUuid: mergedActiveRuns,
        loading: deriveCopilotLoadingFromActiveRuns(mergedActiveRuns)
      };
    }
    case CopilotActions.COPILOT_RUN_STARTED: {
      if (!action.run?.chatUuid) {
        return state;
      }

      const nextActiveRuns = {
        ...state.activeCopilotRunsByChatUuid,
        [action.run.chatUuid]: action.run
      };

      return {
        ...state,
        activeCopilotRunsByChatUuid: nextActiveRuns,
        loading: deriveCopilotLoadingFromActiveRuns(nextActiveRuns)
      };
    }
    case CopilotActions.COPILOT_RUN_EVENT_RECEIVED: {
      const existingRun =
        state.activeCopilotRunsByChatUuid[action.chatUuid] || {};
      if (!action.chatUuid || existingRun.runUuid !== action.runUuid) {
        return state;
      }

      return {
        ...state,
        activeCopilotRunsByChatUuid: {
          ...state.activeCopilotRunsByChatUuid,
          [action.chatUuid]: {
            ...existingRun,
            lastEventId: action.lastEventId ?? existingRun.lastEventId,
            assistantMessageUuid:
              action.assistantMessageUuid ?? existingRun.assistantMessageUuid,
            status: action.status ?? existingRun.status
          }
        }
      };
    }
    case CopilotActions.COPILOT_RUN_CANCEL_REQUESTED: {
      const existingRun = state.activeCopilotRunsByChatUuid[action.chatUuid];
      if (!existingRun || existingRun.runUuid !== action.runUuid) {
        return state;
      }

      return {
        ...state,
        activeCopilotRunsByChatUuid: {
          ...state.activeCopilotRunsByChatUuid,
          [action.chatUuid]: {
            ...existingRun,
            isCancelPending: true
          }
        }
      };
    }
    case CopilotActions.COPILOT_RUN_CANCEL_ROLLED_BACK: {
      const existingRun = state.activeCopilotRunsByChatUuid[action.chatUuid];
      if (!existingRun || existingRun.runUuid !== action.runUuid) {
        return state;
      }

      const { isCancelPending: _isCancelPending, ...restoredRun } = existingRun;
      return {
        ...state,
        activeCopilotRunsByChatUuid: {
          ...state.activeCopilotRunsByChatUuid,
          [action.chatUuid]: restoredRun
        }
      };
    }
    case CopilotActions.COPILOT_RUN_CANCEL_MESSAGES_RECEIVED: {
      const existingSession = state.sessions.data[action.chatUuid];
      if (!existingSession) {
        return state;
      }
      const messages = reconcileCancelledRunMessages(
        existingSession.messages,
        action.messages,
        {
          runUuid: action.runUuid,
          assistantMessageUuid: action.assistantMessageUuid,
          optimisticUserMessageClientRequestId:
            action.optimisticUserMessageClientRequestId
        }
      );
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [action.chatUuid]: {
              ...existingSession,
              messages
            }
          }
        }
      };
    }
    case CopilotActions.COPILOT_RUN_FINISHED: {
      const existingRun = state.activeCopilotRunsByChatUuid[action.chatUuid];
      if (!action.chatUuid || existingRun?.runUuid !== action.runUuid) {
        return state;
      }

      const { [action.chatUuid]: _removedRun, ...remainingRuns } =
        state.activeCopilotRunsByChatUuid;
      const existingSession = state.sessions.data[action.chatUuid];
      const shouldRemoveCancelledRunMessages = action.status === 'cancelled';
      const assistantMessageUuid =
        action.assistantMessageUuid ?? existingRun.assistantMessageUuid;
      const optimisticUserMessageClientRequestId =
        action.optimisticUserMessageClientRequestId ??
        existingRun.optimisticUserMessageClientRequestId;
      const messages = shouldRemoveCancelledRunMessages
        ? reconcileCancelledRunMessages(
            existingSession?.messages,
            action.messages,
            {
              runUuid: action.runUuid,
              assistantMessageUuid,
              optimisticUserMessageClientRequestId
            }
          )
        : existingSession?.messages;
      return {
        ...state,
        sessions:
          shouldRemoveCancelledRunMessages && existingSession
            ? {
                ...state.sessions,
                data: {
                  ...state.sessions.data,
                  [action.chatUuid]: {
                    ...existingSession,
                    messages
                  }
                }
              }
            : state.sessions,
        activeCopilotRunsByChatUuid: remainingRuns,
        fetching: shouldRemoveCancelledRunMessages ? false : state.fetching,
        loading: deriveCopilotLoadingFromActiveRuns(remainingRuns)
      };
    }
    case CopilotActions.MESSAGE_TIMEOUT: {
      return {
        ...state,
        fetching: false,
        loading: deriveCopilotLoadingFromActiveRuns(
          state.activeCopilotRunsByChatUuid
        )
      };
    }
    case CopilotActions.SET_COPILOT_WINDOW_MODE: {
      return {
        ...state,
        windowMode: action.windowMode
      };
    }
    case COPILOT_HOME_PRESENTATION_ACTION: {
      return {
        ...state,
        homePresentation: action.presentation
      };
    }
    case CopilotActions.SET_COPILOT_WINDOW_MODE_FROM_SHELL: {
      return {
        ...state,
        windowMode: action.windowMode,
        urlShellSyncPreserveChat: true
      };
    }
    case CopilotActions.CONSUME_COPILOT_URL_SHELL_PRESERVE_CHAT_FLAG: {
      return {
        ...state,
        urlShellSyncPreserveChat: false
      };
    }
    case CopilotActions.SET_COPILOT_CHAT_CONTEXT: {
      if (!action.chatContext) {
        return {
          ...state,
          chatContext: null,
          composerContextMentionUi: null
        };
      }
      const prevUuid = state.chatContext?.uuid ?? null;
      const nextUuid = action.chatContext.uuid ?? null;
      const sessionChanged = prevUuid !== nextUuid;
      return {
        ...state,
        chatContext: {
          ...action.chatContext,
          contextType: normalizeCopilotContextType(
            action.chatContext.contextType
          )
        },
        ...(sessionChanged ? { composerContextMentionUi: null } : {})
      };
    }
    case CopilotActions.EMPTY_COPILOT_CHAT_CONTEXT: {
      return {
        ...state,
        chatContext: null,
        composerContextMentionUi: null,
        urlShellSyncPreserveChat: false
      };
    }
    case CopilotActions.SET_COPILOT_COMPOSER_CONTEXT_MENTION_UI: {
      return {
        ...state,
        composerContextMentionUi: action.payload
      };
    }
    case CopilotActions.SET_COPILOT_CHAT_SELECTED_CONTEXT: {
      const { chatUuid, selectedContext, selectedMentions } = action;
      const existingChat = state.sessions.data[chatUuid];
      if (!existingChat) {
        return state;
      }
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [chatUuid]: {
              ...existingChat,
              ...(selectedContext !== undefined ? { selectedContext } : {}),
              ...(selectedMentions !== undefined ? { selectedMentions } : {})
            }
          }
        }
      };
    }
    case CopilotActions.INVALIDATE_COPILOT_CHAT_DETAIL_CONTEXT: {
      const { uuid } = action;
      if (!uuid || !state.unifiedCopilotChatDetailsLoaded[uuid]) {
        // Nothing cached to revalidate (never loaded, or already dropped).
        return state;
      }
      return {
        ...state,
        copilotChatDetailContextStaleUuids: {
          ...state.copilotChatDetailContextStaleUuids,
          [uuid]: true
        }
      };
    }
    case CopilotActions.SET_FLOATING_WINDOW_POSITION_AND_SIZE: {
      return {
        ...state,
        floatingWindow: {
          position: action.position ?? state.floatingWindow.position,
          size: action.size ?? state.floatingWindow.size
        }
      };
    }
    default:
      return state;
  }
};

export default copilot;
